import maplibregl, { type Map as MlMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { Compass, Route, Truck, MapPin } from 'lucide-react';

const DARK_MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

// Smooth realistic fleet route across Cairo & 26th July Corridor
const ROUTE_COORDS: [number, number][] = [
  [30.9850, 29.9700], // 6th of October Logistics Hub
  [31.0250, 29.9980],
  [31.0650, 30.0240],
  [31.1050, 30.0440],
  [31.1450, 30.0560], // Mehwar 26th July
  [31.1820, 30.0620], // Lebanon Square / Mohandessin
  [31.2120, 30.0640], // Zamalek / Nile River Bridge
  [31.2357, 30.0444], // Downtown Cairo
  [31.2850, 30.0520], // Nasr City
  [31.3350, 30.0680], // Heliopolis & Ring Road Link
  [31.4200, 30.0250], // New Cairo Fleet Center
];

function getRoutePosition(progress: number): { lon: number; lat: number; bearing: number } {
  const total = ROUTE_COORDS.length - 1;
  const p = Math.max(0, Math.min(1, progress)) * total;
  const idx = Math.min(Math.floor(p), total - 1);
  const frac = p - idx;

  const [lon1, lat1] = ROUTE_COORDS[idx]!;
  const [lon2, lat2] = ROUTE_COORDS[idx + 1]!;

  const lon = lon1 + (lon2 - lon1) * frac;
  const lat = lat1 + (lat2 - lat1) * frac;

  const y = Math.sin(((lon2 - lon1) * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180);
  const x =
    Math.cos((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) -
    Math.sin((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.cos(((lon2 - lon1) * Math.PI) / 180);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;

  return { lon, lat, bearing };
}

export function AuthBackground() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);

  const [telemetry, setTelemetry] = useState({
    speed: 84,
    speedLimit: 90,
    heading: 62,
    lat: 30.0582,
    lon: 31.1554,
    distanceKm: 34.2,
    etaMinutes: 24,
    satellites: 18,
    activeTrucks: 16,
    currentRoad: 'محور 26 يوليو (اتجاه كوبري 15 مايو والقاهرة)',
    nextManeuver: 'بعد 600 متر: الزم المسار الأوسط نحو كوبري 15 مايو',
  });

  useEffect(() => {
    if (!containerRef.current) return;

    // Enable RTL Arabic text shaping for Cairo street and place names
    if (maplibregl.getRTLTextPluginStatus() === 'unavailable') {
      maplibregl.setRTLTextPlugin(
        'https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.js',
        true,
      ).catch(() => undefined);
    }

    let animFrame: number;
    let progress = 0.42;
    let direction = 1;

    // 3D In-Car Navigation Perspective Map (Tilted over Cairo & Nile River)
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DARK_MAP_STYLE,
      center: [31.1950, 30.0580],
      zoom: 13.4,
      pitch: 52, // 3D In-car cockpit navigation pitch
      bearing: 55, // Angle looking along 26th July corridor
      interactive: false,
      attributionControl: false,
    });

    mapRef.current = map;

    // Custom 3D Truck Marker with Headlight Beams & Glowing Beacon
    const markerEl = document.createElement('div');
    markerEl.className = 'relative flex items-center justify-center';
    markerEl.innerHTML = `
      <div class="relative flex items-center justify-center">
        <!-- Forward Headlight Beams -->
        <div class="absolute -top-14 h-16 w-14 -translate-y-2 bg-gradient-to-t from-yellow-300/45 via-yellow-100/20 to-transparent blur-[3px] [clip-path:polygon(25%_100%,75%_100%,100%_0%,0%_0%)]"></div>
        <!-- Pulse Sonar Ring -->
        <div class="absolute size-14 rounded-full border-2 border-[#BEF264] animate-ping opacity-60"></div>
        <!-- Truck Icon Body -->
        <div class="relative flex size-10 items-center justify-center rounded-xl bg-[#0F3D26] border-2 border-[#BEF264] shadow-[0_0_25px_#BEF264]">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#BEF264" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
            <path d="M15 18H9"/>
            <path d="M19 18h2a1 1 0 0 0 1-1v-5l-3-4h-5v10"/>
            <circle cx="7" cy="18" r="2"/>
            <circle cx="17" cy="18" r="2"/>
          </svg>
        </div>
      </div>
    `;

    const setupLayersAndMarker = () => {
      if (!map.getSource('nav-route')) {
        // 1. Add Navigation Route GeoJSON
        map.addSource('nav-route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: ROUTE_COORDS,
            },
          },
        });

        // Neon Lime Route Glow
        map.addLayer({
          id: 'nav-route-glow',
          type: 'line',
          source: 'nav-route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#BEF264',
            'line-width': 18,
            'line-opacity': 0.4,
            'line-blur': 6,
          },
        });

        // Sharp Core Route Line
        map.addLayer({
          id: 'nav-route-core',
          type: 'line',
          source: 'nav-route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#BEF264',
            'line-width': 6,
            'line-opacity': 0.95,
          },
        });

        // Route Casing Dark Border
        map.addLayer(
          {
            id: 'nav-route-casing',
            type: 'line',
            source: 'nav-route',
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#06130B',
              'line-width': 10,
              'line-opacity': 0.75,
            },
          },
          'nav-route-core',
        );
      }

      // 2. Add Live Moving Truck Marker
      if (!markerRef.current) {
        const initialPos = getRoutePosition(progress);
        const marker = new maplibregl.Marker({ element: markerEl, rotationAlignment: 'map' })
          .setLngLat([initialPos.lon, initialPos.lat])
          .setRotation(initialPos.bearing)
          .addTo(map);

        markerRef.current = marker;

        // 3. Smooth Cruise Animation Loop
        let lastTime = performance.now();

        const animateCruise = (now: number) => {
          const delta = (now - lastTime) / 1000;
          lastTime = now;

          // Cruise along the route
          progress += delta * 0.012 * direction;
          if (progress > 0.94) direction = -1;
          if (progress < 0.06) direction = 1;

          const pos = getRoutePosition(progress);
          marker.setLngLat([pos.lon, pos.lat]);
          marker.setRotation(direction === 1 ? pos.bearing : pos.bearing + 180);

          // Smooth camera follow
          map.setCenter([pos.lon, pos.lat]);

          animFrame = requestAnimationFrame(animateCruise);
        };

        animFrame = requestAnimationFrame(animateCruise);
      }
    };

    if (map.isStyleLoaded()) {
      setupLayersAndMarker();
    } else {
      map.once('load', setupLayersAndMarker);
    }

    const interval = setInterval(() => {
      setTelemetry((prev) => {
        const cur = getRoutePosition(progress);
        return {
          ...prev,
          speed: Math.floor(81 + Math.random() * 6),
          heading: Math.floor(cur.bearing < 0 ? cur.bearing + 360 : cur.bearing),
          lat: Number(cur.lat.toFixed(4)),
          lon: Number(cur.lon.toFixed(4)),
        };
      });
    }, 2000);

    return () => {
      cancelAnimationFrame(animFrame);
      clearInterval(interval);
      map.remove();
    };
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
      {/* 1. Real 3D-Tilted MapLibre Navigation Map (Cairo, Nile River, 26th July Corridor) */}
      <div ref={containerRef} className="absolute inset-0 h-full w-full" />

      {/* 2. Tactical Gradient Overlay (Emerald & Deep Slate Vignette for high contrast) */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#06140C] via-transparent to-[#06140C]/75 pointer-events-none" />
      <div className="absolute inset-0 bg-radial from-transparent via-[#06140C]/25 to-[#06140C]/85 pointer-events-none" />

      {/* 3. Top-Start Realistic GPS Navigation Turn Card */}
      <aside className="absolute top-20 start-6 hidden xl:flex flex-col gap-2.5 z-10 animate-fadeIn pointer-events-auto">
        <div className="flex items-center gap-3.5 rounded-2xl border border-white/20 bg-[#0C2417]/92 p-3.5 shadow-[0_15px_35px_rgba(0,0,0,0.5)] backdrop-blur-xl max-w-[370px]">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#BEF264] text-[#0F3D26] shadow-[0_0_25px_rgba(190,242,100,0.55)]">
            <Route size={26} className="rotate-45" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-bold text-[#BEF264]">
              {telemetry.nextManeuver}
            </div>
            <div className="mt-0.5 truncate text-[11px] text-[#9DC5A7]">
              {telemetry.currentRoad}
            </div>
            <div className="mt-1 flex items-center gap-3 text-[10px] font-mono text-[#cde7d3]">
              <span>المسافة: {telemetry.distanceKm} كم</span>
              <span>•</span>
              <span>الوصول: {telemetry.etaMinutes} دقيقة</span>
            </div>
          </div>
        </div>

        {/* Real-time Telemetry Pill Bar */}
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1.5 rounded-full border border-white/15 bg-[#0C2417]/85 px-3 py-1 text-[11px] font-mono text-[#cde7d3] backdrop-blur-md">
            <span className="size-2 rounded-full bg-[#BEF264] animate-ping" />
            <span>اتصال الأقمار: {telemetry.satellites} GNSS</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-white/15 bg-[#0C2417]/85 px-3 py-1 text-[11px] font-mono text-[#cde7d3] backdrop-blur-md">
            <MapPin size={11} className="text-[#BEF264]" />
            <span>{telemetry.lat}° N, {telemetry.lon}° E</span>
          </div>
        </div>
      </aside>

      {/* 4. Bottom-Start Navigation Cockpit HUD (Speedometer & Gyro Compass) */}
      <aside className="absolute bottom-6 start-6 hidden md:flex items-center gap-4 z-10 animate-fadeIn pointer-events-auto">
        <div className="flex items-center gap-3.5 rounded-2xl border border-white/20 bg-[#0C2417]/92 px-4 py-2.5 shadow-2xl backdrop-blur-xl">
          <div className="text-center">
            <span className="block text-2xl font-bold font-mono text-[#BEF264] leading-none">
              {telemetry.speed}
            </span>
            <span className="text-[9px] uppercase tracking-wider text-[#9DC5A7]">كم / ساعة</span>
          </div>
          <div className="h-8 w-px bg-white/15" />
          <div className="flex size-7 items-center justify-center rounded-full border-2 border-red-500 bg-white text-[10px] font-bold text-black font-mono shadow">
            {telemetry.speedLimit}
          </div>
        </div>

        <div className="flex items-center gap-2.5 rounded-2xl border border-white/20 bg-[#0C2417]/92 px-3.5 py-2.5 shadow-2xl backdrop-blur-xl text-[#cde7d3]">
          <Compass size={22} className="text-[#BEF264]" style={{ transform: `rotate(${telemetry.heading}deg)`, transition: 'transform 0.5s ease-out' }} />
          <div>
            <div className="text-xs font-bold font-mono text-white leading-none">
              {telemetry.heading}° NNE
            </div>
            <div className="mt-0.5 text-[9px] text-[#9DC5A7]">اتجاه البوصلة الملاحية</div>
          </div>
        </div>
      </aside>

      {/* 5. Bottom-End Active Fleet Fleet Counter */}
      <aside className="absolute bottom-6 end-6 hidden sm:flex items-center gap-2 rounded-2xl border border-white/20 bg-[#0C2417]/92 px-3.5 py-2 text-xs text-[#cde7d3] shadow-2xl backdrop-blur-xl z-10">
        <Truck size={14} className="text-[#BEF264]" />
        <span>شبكة الملاحة الحية: 16 شاحنة نشطة</span>
      </aside>
    </div>
  );
}
